import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { PDFViewer, PDFLinkService, EventBus } from 'pdfjs-dist/web/pdf_viewer';
import './PdfViewerWithComments.css';
import useLocale from '../hooks/useLocale';

// Указываем путь к воркеру (файл должен лежать в public)
pdfjsLib.GlobalWorkerOptions.workerSrc = process.env.PUBLIC_URL + '/pdf.worker.min.js';

const PdfViewerWithComments = forwardRef((props, ref) => {
  const {
    pdfBlobUrl,
    comments,
    highlightColor = 'rgba(0,255,0,0.3)',
    onCommentSelected,
    onCommentCreated,
    editable,
  } = props;

  const { getCurrentLanguage } = useLocale();
  const lang = getCurrentLanguage();
  const viewerContainerRef = useRef(null);
  const [highlights, setHighlights] = useState([]);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [newCommentData, setNewCommentData] = useState(null);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [newCommentTitle, setNewCommentTitle] = useState('');
  const dragStartRef = useRef(null);
  // While the create-popup is open, we keep a visual highlight overlay here
  const [pendingHighlight, setPendingHighlight] = useState(null); // { left, top, width, height }

  // Создаем eventBus и pdfLinkService один раз
  const eventBusRef = useRef(new EventBus());
  const pdfLinkServiceRef = useRef(new PDFLinkService({ eventBus: eventBusRef.current }));

  const pdfViewer = useRef(null);

  // Функция для вычисления координат выделений
  const computeHighlights = useCallback(() => {
    // компонент уже размонтирован или контейнер ещё не успел отрендериться
    if (!viewerContainerRef.current) {
      console.warn('[PdfViewerWithComments] контейнер ещё не готов или компонент размонтирован');
      return;
    }
    const newHighlights = [];
    if (!pdfViewer.current || !pdfViewer.current._pages || pdfViewer.current._pages.length === 0) {
      console.warn('[PdfViewerWithComments] Страницы ещё не отрисованы');
      return;
    }
    if (!comments) return;
    comments.forEach((comment) => {
      const pageIndex = comment.page - 1;
      const pageView = pdfViewer.current._pages[pageIndex];
      if (!pageView || !pageView.viewport) {
        console.warn(`[PdfViewerWithComments] Нет pageView для страницы ${comment.page}`);
        return;
      }
      const pdfRect = [
        comment.position.x,
        comment.position.y,
        comment.position.x + comment.position.width,
        comment.position.y + comment.position.height,
      ];
      const viewportRect = pageView.viewport.convertToViewportRectangle(pdfRect);
      const [x1, y1, x2, y2] = viewportRect;
      const left = Math.min(x1, x2);
      const top = Math.min(y1, y2);
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);
      const pageElem = pageView.div;
      if (!pageElem) {
        console.warn(`[PdfViewerWithComments] Нет div для страницы ${comment.page}`);
        return;
      }
      const containerRect = viewerContainerRef.current.getBoundingClientRect();
      const pageRect = pageElem.getBoundingClientRect();
      const offsetLeft = pageRect.left - containerRect.left + viewerContainerRef.current.scrollLeft;
      const offsetTop = pageRect.top - containerRect.top + viewerContainerRef.current.scrollTop;
      newHighlights.push({
        id: comment.id,
        left: offsetLeft + left,
        top: offsetTop + top,
        width,
        height,
      });
    });
    setHighlights(newHighlights);
    console.log('[PdfViewerWithComments] Computed highlights:', newHighlights);
  }, [comments]);

  const onHighlightClick = (commentId) => {
    console.log('[PdfViewerWithComments] Highlight clicked:', commentId);
    if (onCommentSelected) onCommentSelected(commentId);
  };

  const initPdf = useCallback(async () => {
    if (!pdfBlobUrl) {
      console.warn('[PdfViewerWithComments] pdfBlobUrl пустой, откладываем инициализацию');
      return;
    }
    if (!viewerContainerRef.current) {
      console.warn('[PdfViewerWithComments] viewerContainerRef.current не доступен');
      return;
    }
    pdfViewer.current = new PDFViewer({
      container: viewerContainerRef.current,
      eventBus: eventBusRef.current,
      linkService: pdfLinkServiceRef.current,
    });
    pdfLinkServiceRef.current.setViewer(pdfViewer.current);

    try {
      // если это http(s)-URL, добавим cache-bust; если это blob: или data:, оставим как есть
      const effectiveUrl =
        typeof pdfBlobUrl === 'string' && /^https?:\/\//i.test(pdfBlobUrl)
          ? `${pdfBlobUrl}${pdfBlobUrl.includes('?') ? '&' : '?'}ts=${Date.now()}`
          : pdfBlobUrl;
      console.log('[PdfViewerWithComments] Loading PDF from URL:', effectiveUrl);
      const loadingTask = pdfjsLib.getDocument(effectiveUrl);
      const pdfDoc = await loadingTask.promise;
      pdfViewer.current.setDocument(pdfDoc);
      pdfLinkServiceRef.current.setDocument(pdfDoc, null);
      // Через небольшую задержку вычисляем выделения один раз после загрузки PDF
      setTimeout(() => {
        computeHighlights();
      }, 500);
    } catch (error) {
      console.error('[PdfViewerWithComments] Ошибка при загрузке PDF:', error);
    }
  }, [pdfBlobUrl]);

  // Эффект для загрузки PDF
  useEffect(() => {
    initPdf();
  }, [pdfBlobUrl, initPdf]);

  // Эффект для обновления выделений при изменении комментариев
  useEffect(() => {
    computeHighlights();
  }, [comments, computeHighlights]);

  // ResizeObserver
  useEffect(() => {
    let resizeObserver = null;
    if (viewerContainerRef.current) {
      resizeObserver = new ResizeObserver(() => {
        setTimeout(() => {
          computeHighlights();
        }, 500); // увеличенная задержка
      });
      resizeObserver.observe(viewerContainerRef.current);
    }
    return () => {
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [computeHighlights]);

  // Recompute highlights when PDF.js scroll position changes (e.g., hyperlink navigation)
  useEffect(() => {
    const el = viewerContainerRef.current;
    if (!el) return;

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        computeHighlights();
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [computeHighlights]);

  const scrollToComment = (commentId) => {
    console.log('[PdfViewerWithComments] scrollToComment called with:', commentId);
    const hl = highlights.find((h) => h.id === commentId);
    if (hl && viewerContainerRef.current) {
      console.log('[PdfViewerWithComments] Scrolling to highlight:', hl);
      viewerContainerRef.current.scrollTo({
        top: hl.top,
        behavior: 'smooth',
      });
    } else {
      console.warn(
        '[PdfViewerWithComments] Highlight not found or container null for commentId:',
        commentId,
      );
    }
  };

  useImperativeHandle(ref, () => ({
    scrollToComment,
  }));

  // Подписка на событие pagesinit
  useEffect(() => {
    const bus = eventBusRef.current;

    const recomputeSoon = () => {
      // allow pdf.js layout to settle
      setTimeout(() => computeHighlights(), 0);
    };

    bus.on('pagesinit', recomputeSoon);
    bus.on('pagechanging', recomputeSoon);
    bus.on('scalechanging', recomputeSoon);
    bus.on('rotationchanging', recomputeSoon);

    return () => {
      bus.off('pagesinit', recomputeSoon);
      bus.off('pagechanging', recomputeSoon);
      bus.off('scalechanging', recomputeSoon);
      bus.off('rotationchanging', recomputeSoon);
    };
  }, [computeHighlights]);

  // Обработчик создания нового комментария по выделению
  const onMouseUp = (e) => {
    // 0) Отпустили не над текстовым слоем - сброс
    const textLayerEl = e.target.closest('.textLayer');
    if (!textLayerEl) {
      return;
    }

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return;

    // утилиты
    const closest = (node, selector) =>
      (node && (node.nodeType === 1 ? node : node.parentElement))?.closest(selector) || null;

    const caretFromPoint = (x, y) => {
      if (document.caretRangeFromPoint) {
        return document.caretRangeFromPoint(x, y);
      }
      if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(x, y);
        if (!pos) return null;
        const r = document.createRange();
        r.setStart(pos.offsetNode, pos.offset);
        r.collapse(true);
        return r;
      }
      return null;
    };

    // 1) Валидируем, что и начало, и конец внутри textLayer одной страницы
    const anchorLayer = closest(sel.anchorNode, '.textLayer');
    const focusLayer = closest(sel.focusNode, '.textLayer');
    if (!anchorLayer || !focusLayer) {
      sel.removeAllRanges();
      setShowCreatePopup(false);
      return;
    }
    const anchorPage = anchorLayer.closest('[data-page-number]');
    const focusPage = focusLayer.closest('[data-page-number]');
    if (!anchorPage || !focusPage || anchorPage !== focusPage) {
      sel.removeAllRanges();
      setShowCreatePopup(false);
      return;
    }

    const pageElem = anchorPage;
    const pageNumber = Number(pageElem.getAttribute('data-page-number'));
    if (!pageNumber) return;

    const pageView =
      pdfViewer.current && pdfViewer.current._pages
        ? pdfViewer.current._pages[pageNumber - 1]
        : null;
    if (!pageView) return;

    // 2) Подменим конец выделения кареткой под курсором (в пределах этой страницы)
    let focusCaret = caretFromPoint(e.clientX, e.clientY);

    // если каретка не попала в текст - притягиваемся к ближайшему спану по Y
    const spans = Array.from(pageElem.querySelectorAll('.textLayer span'));
    const pageRect = pageElem.getBoundingClientRect();

    const makeCaretAtSpanEdge = (span, atEnd = true) => {
      const r = document.createRange();
      let txt = span.firstChild;
      if (!txt) {
        txt = document.createTextNode('');
        span.appendChild(txt);
      }
      const off = atEnd ? (txt.textContent ? txt.textContent.length : 0) : 0;
      r.setStart(txt, off);
      r.collapse(true);
      return r;
    };

    const findNearestSpanByY = (y) => {
      // под курсором
      let exact = spans.find((s) => {
        const r = s.getBoundingClientRect();
        return r.top <= y && r.bottom >= y;
      });
      if (exact) return exact;

      // ближайший сверху / снизу
      let best = null;
      let bestDist = Infinity;
      for (const s of spans) {
        const r = s.getBoundingClientRect();
        const dist = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
        if (dist < bestDist) {
          bestDist = dist;
          best = s;
        }
      }
      return best || spans[spans.length - 1];
    };

    // преобразуем caret в пределы этой страницы и ее textLayer
    const caretInsideSamePage = (range) => {
      if (!range) return false;
      const layer = closest(range.startContainer, '.textLayer');
      const page = layer?.closest('[data-page-number]');
      return !!(layer && page === pageElem);
    };

    if (!caretInsideSamePage(focusCaret)) {
      const snap = findNearestSpanByY(e.clientY);
      focusCaret = makeCaretAtSpanEdge(snap, true); // конец строки
    }

    // 3) Сформируем новый Range, используя "якорь" с mousedown, если он есть
    let anchorCaret = null;
    if (dragStartRef.current && dragStartRef.current.pageElem) {
      anchorCaret = dragStartRef.current.anchorCaret;

      // запретим межстраничный якорь
      const anchorPageOk =
        anchorCaret &&
        (anchorCaret.startContainer?.parentElement || anchorCaret.startContainer)?.closest?.(
          '[data-page-number]',
        ) === pageElem;

      if (!anchorPageOk) {
        sel.removeAllRanges();
        setShowCreatePopup(false);
        return;
      }
    }

    // если по какой-то причине якоря нет - fallback к текущему селекту
    const useAnchorNode = anchorCaret ? anchorCaret.startContainer : sel.anchorNode;
    const useAnchorOffset = anchorCaret ? anchorCaret.startOffset : sel.anchorOffset;

    const newRange = document.createRange();
    try {
      // если focusCaret ДО anchor - инвертируем
      const probe = document.createRange();
      probe.setStart(useAnchorNode, useAnchorOffset);
      probe.collapse(true);
      const before = focusCaret.compareBoundaryPoints(Range.START_TO_START, probe) < 0;

      if (before) {
        newRange.setStart(focusCaret.startContainer, focusCaret.startOffset);
        newRange.setEnd(useAnchorNode, useAnchorOffset);
      } else {
        newRange.setStart(useAnchorNode, useAnchorOffset);
        newRange.setEnd(focusCaret.startContainer, focusCaret.startOffset);
      }
    } catch {
      sel.removeAllRanges();
      setShowCreatePopup(false);
      return;
    }

    // после удачной сборки диапазона - сбросим сохранённый якорь
    dragStartRef.current = null;

    // Подменяем нативную селекцию «правильной»
    sel.removeAllRanges();
    sel.addRange(newRange);

    // 4) Геометрия: берём фрагменты и режем рамками страницы (никаких getBoundingClientRect на весь Range)
    const rects = Array.from(newRange.getClientRects());
    const clipped = rects
      .map((r) => ({
        left: Math.max(r.left, pageRect.left),
        right: Math.min(r.right, pageRect.right),
        top: Math.max(r.top, pageRect.top),
        bottom: Math.min(r.bottom, pageRect.bottom),
      }))
      .filter((r) => r.right > r.left && r.bottom > r.top);

    if (!clipped.length) {
      sel.removeAllRanges();
      setShowCreatePopup(false);
      return;
    }

    // Объединяем
    const left = Math.min(...clipped.map((r) => r.left));
    const right = Math.max(...clipped.map((r) => r.right));
    const top = Math.min(...clipped.map((r) => r.top));
    const bottom = Math.max(...clipped.map((r) => r.bottom));

    // Минимальный размер (анти-микро)
    if (right - left < 2 || bottom - top < 2) {
      sel.removeAllRanges();
      setShowCreatePopup(false);
      return;
    }

    // 5) Переводим в PDF-координаты
    const selLeft = left - pageRect.left;
    const selTop = top - pageRect.top;
    const selRight = right - pageRect.left;
    const selBottom = bottom - pageRect.top;

    const [pdfX1, pdfY1] = pageView.viewport.convertToPdfPoint(selLeft, selTop);
    const [pdfX2, pdfY2] = pageView.viewport.convertToPdfPoint(selRight, selBottom);

    const pdfX = Math.min(pdfX1, pdfX2);
    const pdfY = Math.min(pdfY1, pdfY2);
    const pdfWidth = Math.abs(pdfX2 - pdfX1);
    const pdfHeight = Math.abs(pdfY2 - pdfY1);

    setNewCommentData({
      page: pageNumber,
      position: { x: pdfX, y: pdfY, width: pdfWidth, height: pdfHeight },
    });

    // 6) Pop-up + persistent pending highlight (in container coordinates)
    const containerRect = viewerContainerRef.current.getBoundingClientRect();

    const pending = {
      left: left - containerRect.left + viewerContainerRef.current.scrollLeft,
      top: top - containerRect.top + viewerContainerRef.current.scrollTop,
      width: right - left,
      height: bottom - top,
    };

    setPendingHighlight(pending);

    setPopupPosition({
      top: pending.top + pending.height + 5,
      left: pending.left,
    });

    setNewCommentTitle('');
    setShowCreatePopup(true);
  };

  const onMouseDownWrapper = (e) => {
    // не трогаем попап
    if (e.target.closest('.create-popup')) return;
    // работаем только с ЛКМ
    if (e.button !== 0) return;

    const closest = (node, selector) =>
      (node && (node.nodeType === 1 ? node : node.parentElement))?.closest(selector) || null;

    const textLayerEl = e.target.closest('.textLayer');
    if (!textLayerEl) {
      dragStartRef.current = null;
      return;
    }

    const pageElem = textLayerEl.closest('[data-page-number]');
    if (!pageElem) {
      dragStartRef.current = null;
      return;
    }

    const caretFromPoint = (x, y) => {
      if (document.caretRangeFromPoint) return document.caretRangeFromPoint(x, y);
      if (document.caretPositionFromPoint) {
        const pos = document.caretPositionFromPoint(x, y);
        if (!pos) return null;
        const r = document.createRange();
        r.setStart(pos.offsetNode, pos.offset);
        r.collapse(true);
        return r;
      }
      return null;
    };

    // каретка под курсором как «якорь»; если не попали - привяжем к ближайшему span
    let anchorCaret = caretFromPoint(e.clientX, e.clientY);

    if (!anchorCaret || !closest(anchorCaret.startContainer, '.textLayer')) {
      const spans = Array.from(pageElem.querySelectorAll('.textLayer span'));
      if (spans.length) {
        const y = e.clientY;
        let best = spans[0],
          bestDist = Infinity;
        for (const s of spans) {
          const r = s.getBoundingClientRect();
          const dist = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0;
          if (dist < bestDist) {
            bestDist = dist;
            best = s;
          }
        }
        // каретка в начале ближайшего спана
        const r = document.createRange();
        let txt = best.firstChild || best.appendChild(document.createTextNode(''));
        r.setStart(txt, 0);
        r.collapse(true);
        anchorCaret = r;
      }
    }

    dragStartRef.current = { pageElem, anchorCaret };
  };

  const onMouseUpWrapper = (e) => {
    // если клик/mouseup внутри попапа не трогаем селекцию и попап
    if (e.target.closest('.create-popup')) return;
    onMouseUp(e);
  };

  const confirmCreateComment = () => {
    if (!newCommentTitle.trim()) return;
    const newComment = {
      id: 'c' + Date.now(),
      page: newCommentData.page,
      position: newCommentData.position,
      title: newCommentTitle.trim(),
      thread: [],
    };
    console.log('[PdfViewerWithComments] New comment created:', newComment);
    setShowCreatePopup(false);
    setNewCommentTitle('');
    setNewCommentData(null);
    setPendingHighlight(null);
    window.getSelection().removeAllRanges();
    if (onCommentCreated) onCommentCreated(newComment);
  };

  const cancelCreateComment = () => {
    setShowCreatePopup(false);
    setNewCommentTitle('');
    setNewCommentData(null);
    setPendingHighlight(null);
    window.getSelection().removeAllRanges();
  };

  return (
    <div
      className="pdf-viewer-container"
      ref={viewerContainerRef}
      onMouseDown={editable ? onMouseDownWrapper : undefined}
      onMouseUp={editable ? onMouseUpWrapper : undefined}
    >
      <div className="pdfViewer"></div>
      <div className="highlightContainer">
        {pendingHighlight && (
          <div
            className="highlight pending"
            style={{
              top: pendingHighlight.top + 'px',
              left: pendingHighlight.left + 'px',
              width: pendingHighlight.width + 'px',
              height: pendingHighlight.height + 'px',
              backgroundColor: highlightColor,
            }}
          />
        )}
        {highlights.map((highlight) => (
          <div
            key={highlight.id}
            className="highlight"
            style={{
              top: highlight.top + 'px',
              left: highlight.left + 'px',
              width: highlight.width + 'px',
              height: highlight.height + 'px',
              backgroundColor: highlightColor,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onHighlightClick(highlight.id);
            }}
          ></div>
        ))}
      </div>
      {showCreatePopup && (
        <div
          className="create-popup"
          style={{ top: popupPosition.top + 'px', left: popupPosition.left + 'px' }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            value={newCommentTitle}
            onChange={(e) => setNewCommentTitle(e.target.value)}
            placeholder={lang === 'ru' ? 'Введите заголовок комментария' : 'Enter comment title'}
            className="create-input"
          />
          <div className="create-buttons">
            <button
              className="confirm-button"
              onClick={(e) => {
                e.stopPropagation();
                confirmCreateComment();
              }}
            >
              {lang === 'ru' ? 'Создать комментарий' : 'Create comment'}
            </button>
            <button
              className="cancel-button"
              onClick={(e) => {
                e.stopPropagation();
                cancelCreateComment();
              }}
            >
              {lang === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

export default React.memo(PdfViewerWithComments);
